<script lang="ts">
import { useI18n } from 'vue-i18n';
import { QTable } from 'quasar';
import { defineAsyncComponent, defineComponent, PropType, ref } from 'vue';
import { StatementStatus, FileFormat } from '@/app/graphql/statement/sdk.statement';
import { Statement } from '@/shared/services/statement';

import UiPlaceholderEmpty from '@/shared/ui/placeholder/empty';
import IconDownload from '@/shared/assets/icons/download.vue';

const UiPlaceholderError = defineAsyncComponent(() => import('@/shared/ui/placeholder/error'));

export default defineComponent({
  name: 'FeatureDocumentsAccountStatementList',
  components: { IconDownload, UiPlaceholderEmpty, UiPlaceholderError },
  props: {
    modelValue: {
      type: Array as PropType<Statement[]>,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['download', 'nextStatements', 'refresh-statement'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const statementListRef = ref<QTable>();
    const statusStatement = { ...StatementStatus };
    const fileFormat = { ...FileFormat };

    const onScroll = (index: number, done: Function) => emit('nextStatements', done);

    return {
      t,
      statusStatement,
      statementListRef,
      fileFormat,
      onScroll,
    };
  },
});
</script>

<template>
  <div v-if="loading && !modelValue.length" class="ob-mt-md">
    <q-skeleton v-for="n in 3" :key="n" height="80px" class="skeleton-loading ob-mb-lg" />
  </div>
  <ui-placeholder-error
    v-else-if="!loading && error"
    :label="t('DOCUMENTS.STATEMENT_ACCOUNT.ERROR')"
    :has-retry="true"
    @retried="$emit('refresh-statement')"
  />
  <ui-placeholder-empty v-else-if="!loading && !modelValue.length" :label="t('DOCUMENTS.STATEMENT_ACCOUNT.EMPTY')" />
  <q-infinite-scroll v-else @load="onScroll">
    <div class="statement-wrapper">
      <div v-for="(statement, index) in modelValue" :key="index" class="statement">
        <div class="statement__content">
          <div class="statement__account">
            <span v-if="statement.availableAccounts.length === 1">{{ statement.availableAccounts[0].account }}</span>
            <span v-else>{{ t('DOCUMENTS.STATEMENT_ACCOUNT.ALL_ACCOUNT') }}</span>
          </div>
          <div class="statement__periods">{{ statement.startDate }} - {{ statement.endDate }}</div>
          <span class="statement__status" :class="statement.statusLabel.classList">
            {{ t(statement.statusLabel.label) }}
          </span>
        </div>

        <div class="statement__actions">
          <div class="statement__date">{{ statement.requestDate }}</div>

          <div class="ob-mt-auto">
            <q-btn
              flat
              dense
              :disable="statement.status !== statusStatement.Ready"
              @click="$emit('download', { type: fileFormat.Pdf, statement })"
            >
              <icon-download :disable="statement.status !== statusStatement.Ready" />
            </q-btn>
          </div>
        </div>
      </div>
    </div>
  </q-infinite-scroll>
</template>

<style scoped lang="scss">
.statement {
  display: flex;
  justify-content: space-between;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid $grey-light;

  &-wrapper > div:last-child {
    border: 0;
    margin-bottom: 60px;
    padding-bottom: 0;
  }

  &__account {
    margin-bottom: 2px;
  }

  &__periods,
  &__date {
    margin-bottom: 4px;
    font-size: 14px;
    color: $secondary-dark;
  }

  &__status {
    padding: 3px 10px;
    font-size: 12px;
    text-align: center;
    border-radius: 30px;
    background: transparent;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-end;
  }
}
</style>
